import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {useGetProductionGuardMatchResults} from '../../../hooks/productions/useProductions';
import {
  GetProductionGuardMatchResultsPayload,
  ProductionGuardQuery,
} from '../../../api/types/production.type';
import ArrButton from '../../../components/common/arr-button/ArrButton';
import ArrToast from '../../../components/common/arr-toast/ArrToast';
import {TOAST_DURATION} from '../../../shared/constants';

const ProductionGuardMatchResultsApiTest: React.FC = () => {
  // Request parameters
  const [productionId, setProductionId] = useState<string | number>(59);
  const [securityGroupId, setSecurityGroupId] = useState<string | number>(1);
  const [isMarkComplete, setIsMarkComplete] = useState<boolean>(false);

  // Query parameters
  const [sequenceNumber, setSequenceNumber] = useState<string | number>('1');
  const [criteriaForUI, setCriteriaForUI] = useState<string>(
    'tags: [ name: ["Attorney_Client", "Attorney_WorkProduct"] ]'
  );
  const [criteriaForDB, setCriteriaForDB] = useState<string>(
    'tags: [ name: ["Attorney_Client", "Attorney_WorkProduct"] ]'
  );
  const [operatorCode, setOperatorCode] = useState<string>('');

  // Response state
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastStatus, setToastStatus] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('success');
  const [toastMessage, setToastMessage] = useState<string>('');

  const {
    data: matchResultsResponse,
    mutate: getMatchResults,
    error: matchError,
    isPending,
  } = useGetProductionGuardMatchResults(
    (data) => {
      setShowToast(true);
      setToastStatus('success');
      setToastMessage('Match results retrieved successfully');
      console.log('Match results:', data);
    },
    (error) => {
      setShowToast(true);
      setToastStatus('error');
      setToastMessage(error.message || 'Failed to get match results');
      console.error('Get match results failed:', error);
    }
  );

  const handleGetMatchResults = () => {
    const queries: ProductionGuardQuery[] = [
      {
        SequenceNumber: sequenceNumber,
        CriteriaForUI: criteriaForUI,
        CriteriaForDB: criteriaForDB,
        OperatorCode: operatorCode,
      },
    ];

    const payload: GetProductionGuardMatchResultsPayload = {
      ProductionId: productionId,
      IsMarkComplete: isMarkComplete,
      SecurityGroupId: securityGroupId,
      lstProductionGuardQueries: queries,
    };

    getMatchResults(payload);
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h5"
        className="typo-text headline3_size"
        gutterBottom
      >
        Production Guard Match Results
      </Typography>
      <Box sx={{display: 'grid', gap: 2, maxWidth: 800, marginTop: 2}}>
        {/* Basic Information */}
        <Typography variant="h6" className="typo-text subhead2_size">
          Basic Information
        </Typography>

        <TextField
          label="Production Id"
          type="text"
          value={productionId}
          onChange={(e) => setProductionId(e.target.value)}
          size="small"
        />

        <TextField
          label="Security Group Id"
          type="text"
          value={securityGroupId}
          onChange={(e) => setSecurityGroupId(e.target.value)}
          size="small"
        />

        <FormControlLabel
        data-testid="is-mark-complete"
          control={
            <Switch
              checked={isMarkComplete}
              onChange={(e) => setIsMarkComplete(e.target.checked)}
            />
          }
          label="Is Mark Complete"
        />

        {/* Production Guard Queries */}
        <Typography
          variant="h6"
          className="typo-text subhead2_size"
          sx={{marginTop: 2}}
        >
          Production Guard Queries
        </Typography>

        <TextField
          label="Sequence Number"
          type="text"
          value={sequenceNumber}
          onChange={(e) => setSequenceNumber(e.target.value)}
          size="small"
        />

        <TextField
          label="Criteria For UI"
          type="text"
          value={criteriaForUI}
          onChange={(e) => setCriteriaForUI(e.target.value)}
          size="small"
          multiline
          rows={3}
        />

        <TextField
          label="Criteria For DB"
          type="text"
          value={criteriaForDB}
          onChange={(e) => setCriteriaForDB(e.target.value)}
          size="small"
          multiline
          rows={3}
        />

        <TextField
          label="Operator Code"
          type="text"
          value={operatorCode}
          onChange={(e) => setOperatorCode(e.target.value)}
          size="small"
        />

        {/* Action Buttons */}
        <Box sx={{display: 'flex', gap: 1, marginTop: 2}}>
          <ArrButton
            onClick={handleGetMatchResults}
            disabled={isPending}
            dataTestId="production-guard-match-results-api-test-button">
            {isPending ? 'Getting Match Results...' : 'Get Match Results'}
          </ArrButton>
        </Box>

        {/* Response Display */}
        {matchResultsResponse && (
          <Box
            sx={{
              backgroundColor: '#f0f8ff',
              padding: 2,
              borderRadius: 1,
              marginTop: 2,
            }}
          >
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              gutterBottom
            >
              Guard Information:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#fff',
                padding: 2,
                borderRadius: 1,
                marginTop: 1,
              }}
            >
              <Box sx={{display: 'grid', gap: 1}}>
                <Typography className="typo-text body3_size">
                  <strong>Total Documents Count:</strong>{' '}
                  {matchResultsResponse.guardInformation.totalDocumentsCount}
                </Typography>
                <Typography className="typo-text body3_size">
                  <strong>Family Documents Count:</strong>{' '}
                  {matchResultsResponse.guardInformation.familyDocumentsCount}
                </Typography>
                <Typography className="typo-text body3_size">
                  <strong>Show Navigation Buttons:</strong>{' '}
                  {matchResultsResponse.guardInformation.showNavigationButtons
                    ? 'Yes'
                    : 'No'}
                </Typography>
                <Typography className="typo-text body3_size">
                  <strong>Does Contain Privilege Docs:</strong>{' '}
                  {matchResultsResponse.guardInformation
                    .doesContainPrivilegeDocs
                    ? 'Yes'
                    : 'No'}
                </Typography>
              </Box>
            </Box>
            <Box
              component="pre"
              sx={{
                backgroundColor: '#fff',
                padding: 2,
                borderRadius: 1,
                overflow: 'auto',
                fontSize: '12px',
                marginTop: 2,
              }}
            >
              {JSON.stringify(matchResultsResponse, null, 2)}
            </Box>
          </Box>
        )}

        {matchError && (
          <Box
            sx={{
              backgroundColor: '#fff3e0',
              padding: 2,
              borderRadius: 1,
              marginTop: 2,
            }}
          >
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              color="error"
              gutterBottom
            >
              Error:
            </Typography>
            <Typography className="typo-text body3_size" color="error">
              {(matchError as Error).message}
            </Typography>
          </Box>
        )}
      </Box>
      {/* Toast Notification */}
      {showToast && (
        <ArrToast
          open={showToast}
          message={toastMessage}
          severity={toastStatus}
          onClose={() => setShowToast(false)}
          duration={TOAST_DURATION}
        />
      )}
    </Box>
  );
};

export default ProductionGuardMatchResultsApiTest;
